"use client";

// CellThreads — THE line, drawn (law_cell_thread · lib/brand/thread.ts).
//
// A layer that fills its parent and stamps the given threads into one canvas,
// with the same bitmap tinted and blurred once beneath it as the glow. It
// redraws only when its size or its threads change; anything that moves (an
// orbit turning, a reveal) moves the LAYER with a css transform or clip, never
// a redraw.
//
// Colour is css's: the cells take the layer's `color` (default: the ink, so a
// thread follows the ground and the organism's hue) and the glow takes
// `--thread-glow` (default: the organism's own colour). Change either in css
// and the next draw follows; a hue change mid-session redraws on its own.

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { paintThreads, threadDpr, type Thread } from "../../lib/brand/thread";
import "./cell-threads.css";

export type ThreadsOf = readonly Thread[] | ((w: number, h: number) => readonly Thread[]);

/** set a css colour on a context, falling back when this browser's canvas cannot read it
 *  (an `oklab()` or `color-mix()` a canvas does not know leaves the old style in place) */
function fillWith(ctx: CanvasRenderingContext2D, color: string, fallback: string) {
  const SENTINEL = "#010203";
  ctx.fillStyle = SENTINEL;
  ctx.fillStyle = color;
  if (String(ctx.fillStyle).toLowerCase() === SENTINEL && color.toLowerCase() !== SENTINEL) ctx.fillStyle = fallback;
}

export default function CellThreads({
  threads,
  glow = true,
  className,
  style,
  onDrawn,
}: {
  /** the threads in this layer's own px, or a function of its size */
  threads: ThreadsOf;
  glow?: boolean;
  className?: string;
  style?: CSSProperties;
  /** told how many cells were stamped (tests and the perf note read it) */
  onDrawn?: (cells: number) => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inkRef = useRef<HTMLCanvasElement | null>(null);
  const glowRef = useRef<HTMLCanvasElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [hue, setHue] = useState("");

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const put = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w > 0 && h > 0) setDims((d) => (d && d.w === w && d.h === h ? d : { w, h }));
    };
    put();
    const ro = new ResizeObserver(put);
    ro.observe(el);
    // the ink is derived from the organism's hue, which drifts: re-read it now
    // and then (cheap), and redraw only when it actually moved
    const hueAt = () => setHue(getComputedStyle(el).color);
    hueAt();
    const t = window.setInterval(hueAt, 4000);
    return () => {
      ro.disconnect();
      window.clearInterval(t);
    };
  }, []);

  useEffect(() => {
    const box = boxRef.current;
    const ink = inkRef.current;
    if (!box || !ink || !dims) return;
    const dpr = threadDpr();
    ink.width = Math.round(dims.w * dpr);
    ink.height = Math.round(dims.h * dpr);
    const ctx = ink.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, ink.width, ink.height);
    const cs = getComputedStyle(box);
    fillWith(ctx, cs.color || "#000", "#2c4a78");
    const list = typeof threads === "function" ? threads(dims.w, dims.h) : threads;
    const cells = paintThreads(ctx, list, dpr);
    const g = glowRef.current;
    if (g && glow && ink.width > 0 && ink.height > 0) {
      g.width = ink.width;
      g.height = ink.height;
      const gx = g.getContext("2d");
      if (gx) {
        // the glow is the same marks in the organism's colour: one copy, one
        // fill through them, then css blurs the whole layer once
        gx.clearRect(0, 0, g.width, g.height);
        // BAKE THE BLUR ONCE. A css filter on the glow canvas is re-rasterised whenever the layer
        // moves or the page composites it (a long page scrolling past an orbit dropped frames to
        // 4 fps on exactly that). So where the canvas can blur, the halo is blurred here, once per
        // draw, and the css filter is switched off (data-baked); elsewhere the css blur stays.
        delete g.dataset.baked; // read the css blur itself, not the one switched off last time
        const css = getComputedStyle(g).filter;
        const px = /blur\(([\d.]+)px\)/.exec(css || "")?.[1];
        let baked = false;
        if (px) {
          gx.filter = `blur(${(Number(px) * dpr).toFixed(2)}px)`;
          baked = gx.filter !== "none" && gx.filter !== "" && typeof gx.filter === "string";
        }
        gx.drawImage(ink, 0, 0);
        gx.filter = "none";
        if (baked) g.dataset.baked = "";
        else delete g.dataset.baked;
        gx.globalCompositeOperation = "source-in";
        fillWith(gx, cs.getPropertyValue("--thread-glow").trim() || cs.color || "#000", "#5a8fce");
        gx.fillRect(0, 0, g.width, g.height);
        gx.globalCompositeOperation = "source-over";
      }
    }
    onDrawn?.(cells);
  }, [dims, threads, glow, hue, onDrawn]);

  return (
    <div ref={boxRef} className={`cell-threads${className ? " " + className : ""}`} style={style} aria-hidden="true">
      {glow ? <canvas ref={glowRef} className="cell-threads__cv cell-threads__cv--glow" /> : null}
      <canvas ref={inkRef} className="cell-threads__cv" />
    </div>
  );
}
